// @ts-check
import { overlaps, hurt } from './mechanisms.js';
import { playerBody } from './power.js';
import { moveActor } from './actors.js';
/** @typedef {import('./types.js').Boss} Boss */
/** @typedef {import('./types.js').Level} Level */
/** @typedef {import('./player.js').Position} Player */
/** @param {import('./types.js').BossDefinition} definition @returns {Boss} */
export function createBoss(definition) {
  return { definition, x: definition.x, y: 0, vx: 0, vy: 0, width: definition.kind === 'reclaimer' ? 100 : 72, height: definition.kind === 'clockwarden' ? 92 : 64,
    health: definition.health, phase: 'dormant', age: 0, cycle: 0, target: 0, side: -1, cue: 'Approach the security core', attacks: [] };
}
/** @param {Boss} boss @param {import('./types.js').EventSink} [emit] */
function hit(boss, emit) {
  if (boss.phase === 'stagger' || boss.phase === 'defeated') return;
  boss.health--; boss.age = 0; boss.attacks.length = 0;
  boss.phase = boss.health <= 0 ? 'defeated' : 'stagger';
  boss.cue = boss.health <= 0 ? 'System restored. Uplink unlocked.' : 'Coupling damaged';
  emit?.({ type: boss.health <= 0 ? 'boss-defeated' : 'boss-hit', x: boss.x + boss.width / 2, y: boss.y + boss.height, entityId: `coupling-${boss.health}` });
}
/** A physical arena node is the repeatable counter for router and shield bosses.
 * @param {Level} level @param {import('./types.js').EventSink} [emit]
 */
export function counterBoss(level, emit) {
  const boss = level.boss;
  if (!boss || !['attack', 'open'].includes(boss.phase)) return;
  if (['gatekeeper', 'switchboard', 'raster'].includes(boss.definition.kind) || boss.definition.kind === 'null' && pattern(boss) === 'switchboard') hit(boss, emit);
}
/** @param {Boss} boss @param {import('./types.js').EventSink} [emit] */
export function pulseBoss(boss, emit) { if (boss.phase === 'open') hit(boss, emit); }
/** @param {Boss} boss @returns {import('./types.js').BossKind} */
function pattern(boss) {
  if (boss.definition.kind !== 'null') return boss.definition.kind;
  return boss.health > 4 ? 'switchboard' : boss.health > 2 ? 'watchdog' : 'heatsink';
}
/** @param {Boss} boss @param {number} x @param {number} y @param {number} vx @param {number} width @param {number} height @param {import('./types.js').Attack['kind']} kind @param {number} [warning] */
function attack(boss, x, y, vx, width, height, kind, warning = 0) { boss.attacks.push({ x, y, vx, vy: 0, width, height, kind, warning, age: 0, life: vx ? 3 : warning + 0.55 }); }
/** Boss attacks, tells, vulnerability, and defeat are independent of ordinary
 * bot health, Overclock contacts, and rolling cores. Every fight has a base-QP counter.
 * @param {Level} level @param {Player} player @param {number} oldY @param {number} dt @param {import('./types.js').EventSink} [emit]
 */
export function updateBoss(level, player, oldY, dt, emit) {
  const b = level.boss;
  if (!b || b.phase === 'defeated') return;
  const d = b.definition;
  if (b.phase === 'dormant') {
    if (player.x < d.left + 12 || b.x - player.x > 160) return;
    b.phase = 'tell'; b.age = 0; b.target = player.x; b.side = player.x < b.x ? -1 : 1;
  }
  b.age += dt;
  const kind = pattern(b);
  const tell = kind === 'watchdog' ? 1.05 : 1.35;
  b.vx = 0;
  if (b.phase === 'tell') {
    b.cue = { gatekeeper: 'Jump the charge. Strike the purple counter node.', switchboard: 'Packets inbound. Strike the routing node.', reclaimer: 'Reclaim sweep. Jump, then pound the open cap.', clockwarden: 'Three pulses. Jump the low beam, then the latch.', raster: 'Follow the wired emitter. Strike the layer node.', heatsink: 'Heat rising. Cooling opens the coupling.', watchdog: 'Lock-on. Jump the charge, then stomp the core.', null: '' }[kind];
    if (b.age >= tell) {
      b.phase = 'attack'; b.age = 0; b.target = player.x; b.side = player.x < b.x ? -1 : 1;
      if (kind === 'switchboard') for (let i = 0; i < 3; i++) attack(b, b.x + 30 - b.side * i * 40, 14 + i % 2 * 54, b.side * 120, 22, 10, 'packet', i * 0.22);
      if (kind === 'reclaimer') for (let i = 0; i < 4; i++) attack(b, d.left + 42 + (b.cycle % 2 ? 3 - i : i) * 65, 0, 0, 48, 22, 'wave', 0.3 + i * 0.26);
      if (kind === 'clockwarden') for (let i = 0; i < 3; i++) attack(b, b.x + 30 - b.side * i * 52, i === 1 ? 74 : 8, b.side * 145, 14, 12, 'beam', i * 0.3);
      if (kind === 'raster') for (let i = 0; i < 2; i++) attack(b, b.target + (i - 0.5) * 80, 0, 0, 32, 82, 'beam', 0.7 + i * 0.2);
      if (kind === 'heatsink') for (let i = 0; i < 3; i++) attack(b, d.left + 40 + i * 94, 0, 0, 36, 70, 'wave', 0.55 + i * 0.15);
    }
  } else if (b.phase === 'attack') {
    if (kind === 'gatekeeper' || kind === 'watchdog') {
      const speed = kind === 'watchdog' ? 210 : 140;
      b.vx = b.side * speed;
    }
    if (b.age > (kind === 'watchdog' ? 1.05 : 1.45)) { b.phase = 'open'; b.age = 0; b.cue = 'Coupling exposed. Stomp the glowing cap!'; }
  } else if (b.phase === 'open' && b.age > 2.5 || b.phase === 'stagger' && b.age > 0.7) {
    b.phase = 'tell'; b.age = 0; b.cycle++;
  }
  const travel = b.vx;
  moveActor(b, level, dt);
  b.x = Math.max(d.left + 20, Math.min(d.right - b.width - 20, b.x));
  if (travel && Math.sign(b.vx) !== Math.sign(travel)) { b.phase = 'open'; b.age = 0; b.cue = 'Charge stopped by armor. Stomp the open coupling!'; }
  const body = { ...playerBody(player), x: player.x, y: player.y };
  if (overlaps(body, b)) {
    if (b.phase === 'open' && player.vy < 0 && oldY >= b.y + b.height - 8) {
      hit(b, emit); player.vy = 380; player.grounded = false; player.jumps = 1; player.pound = 'none';
    } else if (b.phase !== 'stagger') hurt(level, player, b, `Disabled by ${d.name}`, emit);
  }
  for (const a of b.attacks) {
    a.age += dt;
    if (a.age < a.warning) continue;
    a.x += a.vx * dt;
    if (overlaps(body, a)) hurt(level, player, a, `Disabled by ${d.name}`, emit);
  }
  b.attacks = b.attacks.filter(a => a.age < a.life && a.x > d.left - 80 && a.x < d.right + 80);
}
